import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { apiConfig } from '../../api/ApiConfig';
import { molDockHeaders } from '../../configs/GridConfig';
import { useNavigate, useParams } from 'react-router-dom';
import { FieldGroupWrapper } from '../../style/StyledElement';
import SpinnerButton from '../../components/common/SpinnerButton';
import UploadButton from '../../components/common/UploadButton';
import LinearWithValueLabel from '../../components/common/loadingAnimation';
import AgGridWrapper from '../../components/common/AgGridWrapper';
import {
  Typography,
  Box,
  Alert,
  Snackbar,
  Paper,
  Divider,
  Chip,
  Card,
  CardContent
} from '@mui/material';
import { styled } from '@mui/material/styles';
import FileModal from '../../components/common/FileModal';
import { v4 as uuidv4 } from 'uuid';
import { API_BASE_URL } from '../../configs/Constants';

// ============================================================================
// CONSTANTS
// ============================================================================
const SEARCH_STATES = {
  INIT: 'INIT',
  LOADING: 'LOADING',
  COMPLETE: 'COMPLETE',
  ERROR: 'ERROR'
};

const FILE_TYPES = {
  SMILE: 'smileFile',
  PDB: 'pdbFile'
};

const UPLOAD_NAMES = {
  SMILE: 'Upload SMILES File',
  PDB: 'Upload PDB File'
};

// ============================================================================
// STYLED COMPONENTS - Professional Space Grey Theme
// ============================================================================
const HeaderContainer = styled(Paper)(({ theme }) => ({
  background: 'linear-gradient(135deg, #8B909A 0%, #6B7280 100%)',
  color: '#FFFFFF',
  padding: theme.spacing(3, 4),
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(3),
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
}));

const ContentContainer = styled(Paper)(({ theme }) => ({
  background: '#FFFFFF',
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
  border: '1px solid #E5E7EB',
  marginBottom: theme.spacing(2),
}));

const UploadSection = styled(Card)(({ theme }) => ({
  background: '#F9FAFB',
  border: '1px solid #E5E7EB',
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(3),
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
}));

const ResultsSection = styled(Box)(({ theme }) => ({
  background: '#FFFFFF',
  border: '1px solid #E5E7EB',
  borderRadius: theme.spacing(2),
  overflow: 'hidden',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
}));

const FileUploadCard = styled(Paper)(({ theme }) => ({
  background: '#FFFFFF',
  border: '1px solid #E5E7EB',
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2.5),
  marginBottom: theme.spacing(2),
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
    borderColor: '#D1D5DB',
  }
}));

const FileContentBox = styled(Box)(({ theme, hasContent }) => ({
  minHeight: hasContent ? 180 : 80,
  border: '2px dashed',
  borderColor: hasContent ? '#6B7280' : '#D1D5DB',
  borderRadius: theme.spacing(1.5),
  padding: theme.spacing(2),
  backgroundColor: hasContent ? '#F8FAFC' : '#F9FAFB',
  display: 'flex',
  alignItems: hasContent ? 'flex-start' : 'center',
  justifyContent: hasContent ? 'flex-start' : 'center',
  overflow: 'hidden',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    borderColor: hasContent ? '#4B5563' : '#9CA3AF',
    backgroundColor: hasContent ? '#F1F5F9' : '#F3F4F6',
  }
}));

const FeatureChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  color: '#FFFFFF',
  fontWeight: 500,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
  }
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  const getStatusColors = () => {
    switch (status) {
      case 'uploaded':
        return {
          backgroundColor: '#DCFCE7',
          color: '#166534',
          border: '1px solid #BBF7D0'
        };
      case 'processing':
        return {
          backgroundColor: '#FEF3C7',
          color: '#92400E',
          border: '1px solid #FDE68A'
        };
      case 'error':
        return {
          backgroundColor: '#FEE2E2',
          color: '#DC2626',
          border: '1px solid #FECACA'
        };
      default:
        return {
          backgroundColor: '#F3F4F6',
          color: '#6B7280',
          border: '1px solid #D1D5DB'
        };
    }
  };
  
  return getStatusColors();
});

const ActionButton = styled(SpinnerButton)(({ theme, variant = 'primary' }) => {
  const getButtonStyles = () => {
    switch (variant) {
      case 'primary':
        return {
          background: 'linear-gradient(135deg, #8B909A 0%, #6B7280 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)',
          }
        };
      case 'secondary':
        return {
          backgroundColor: '#F9FAFB',
          color: '#374151',
          border: '1px solid #D1D5DB',
          '&:hover': {
            backgroundColor: '#F3F4F6',
            borderColor: '#9CA3AF',
          }
        };
      case 'danger':
        return {
          backgroundColor: '#FEE2E2',
          color: '#DC2626',
          border: '1px solid #FECACA',
          '&:hover': {
            backgroundColor: '#FECACA',
            borderColor: '#F87171',
          }
        };
      default:
        return {};
    }
  };
  
  return {
    fontWeight: 600,
    textTransform: 'none',
    borderRadius: theme.spacing(1),
    ...getButtonStyles()
  };
});

// ============================================================================
// FILE VALIDATION UTILITIES
// ============================================================================
const validateFile = (file, fileType) => {
  if (!file) return { isValid: false, error: 'No file selected' };
  
  const maxSize = 10 * 1024 * 1024; // 10MB
  if (file.size > maxSize) {
    return { isValid: false, error: 'File size exceeds 10MB limit' };
  }

  // Basic file type validation
  if (fileType === FILE_TYPES.PDB) {
    if (!file.name.toLowerCase().endsWith('.pdb')) {
      return { isValid: false, error: 'Please select a valid PDB file (.pdb)' };
    }
  } else if (fileType === FILE_TYPES.SMILE) {
    const validExtensions = ['.smi', '.smiles', '.txt'];
    const hasValidExtension = validExtensions.some(ext => 
      file.name.toLowerCase().endsWith(ext)
    );
    if (!hasValidExtension) {
      return { isValid: false, error: 'Please select a valid SMILES file (.smi, .smiles, .txt)' };
    }
  }

  return { isValid: true, error: null };
};

// ============================================================================
// CUSTOM HOOKS
// ============================================================================
const useFileUpload = () => {
  const [files, setFiles] = useState({
    smile: { content: null, name: null },
    pdb: { content: null, name: null }
  });
  const [previews, setPreviews] = useState({
    smile: false,
    pdb: false
  });
  const [error, setError] = useState(null);

  const smileFileInputRef = useRef(null);
  const pdbFileInputRef = useRef(null);

  const handleFileUpload = useCallback(async (event, fileType) => {
    const file = event.target.files[0];
    if (!file) return;

    const validation = validateFile(file, fileType);
    if (!validation.isValid) {
      setError(validation.error);
      return;
    }

    try {
      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target.result;
        setFiles(prev => ({
          ...prev,
          [fileType === FILE_TYPES.SMILE ? 'smile' : 'pdb']: {
            content,
            name: file.name
          }
        }));
      };
      reader.onerror = () => {
        setError('Failed to read file. Please try again.');
      };
      reader.readAsText(file);
    } catch (err) {
      setError('Error processing file: ' + err.message);
    }
  }, []);

  const removeFile = useCallback((fileType) => {
    const key = fileType === FILE_TYPES.SMILE ? 'smile' : 'pdb';
    const inputRef = fileType === FILE_TYPES.SMILE ? smileFileInputRef : pdbFileInputRef;
    
    setFiles(prev => ({
      ...prev,
      [key]: { content: null, name: null }
    }));
    
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    
    setPreviews(prev => ({
      ...prev,
      [key]: false
    }));
  }, []);

  const togglePreview = useCallback((fileType) => {
    const key = fileType === FILE_TYPES.SMILE ? 'smile' : 'pdb';
    setPreviews(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    files,
    previews,
    error,
    smileFileInputRef,
    pdbFileInputRef,
    handleFileUpload,
    removeFile,
    togglePreview,
    clearError
  };
};

// ============================================================================
// ENHANCED FILE UPLOAD COMPONENT
// ============================================================================
const FileUploadBox = ({
  uploadedFile,
  onFileChange,
  uploadName,
  fileInputRef,
  onRemove,
  onPreview,
  fileType
}) => {
  const hasContent = uploadedFile?.content;
  const fileTypeName = fileType === FILE_TYPES.SMILE ? 'SMILES' : 'PDB';
  const displayContent = hasContent ? 
    (uploadedFile.content.length > 500 ? 
      uploadedFile.content.substring(0, 500) + '...' : 
      uploadedFile.content) : 
    `No ${fileTypeName} file uploaded`;

  const getFileSize = () => {
    if (!hasContent) return '';
    const size = new Blob([uploadedFile.content]).size;
    return size > 1024 ? `${(size / 1024).toFixed(1)} KB` : `${size} B`;
  };

  return (
    <FileUploadCard elevation={0}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" sx={{ color: '#374151', fontWeight: 600 }}>
          {uploadName}
        </Typography>
        {hasContent && (
          <StatusChip 
            status="uploaded"
            label={`✓ Uploaded`} 
            size="small"
          />
        )}
      </Box>
      
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <FileContentBox hasContent={hasContent} sx={{ flex: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              color: hasContent ? '#374151' : '#6B7280',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontSize: '0.85rem',
              lineHeight: 1.4
            }}
          >
            {displayContent}
          </Typography>
        </FileContentBox>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 200 }}>
          <UploadButton
            onChange={(e) => onFileChange(e, fileType)}
            UploadName={hasContent ? 'Replace File' : uploadName}
            inputRef={fileInputRef}
            sx={{
              backgroundColor: '#8B909A',
              color: 'white',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#6B7280',
              }
            }}
          />
          
          {hasContent && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <Chip 
                  label={uploadedFile.name} 
                  size="small" 
                  variant="outlined"
                  sx={{ 
                    backgroundColor: '#F8FAFC',
                    borderColor: '#D1D5DB',
                    color: '#374151',
                    maxWidth: '150px'
                  }}
                />
                <Typography variant="caption" sx={{ color: '#6B7280' }}>
                  {getFileSize()}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <ActionButton
                  variant="secondary"
                  label="Preview"
                  onClick={onPreview}
                  size="small"
                  sx={{ flex: 1 }}
                />
                <ActionButton
                  variant="danger"
                  label="Remove"
                  onClick={onRemove}
                  size="small"
                  sx={{ flex: 1 }}
                />
              </Box>
            </>
          )}
        </Box>
      </Box>
    </FileUploadCard>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export const MolDock = () => {
  const [searchState, setSearchState] = useState(SEARCH_STATES.INIT);
  const [searchResult, setSearchResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [logs, setLogs] = useState([]);
  
  const navigate = useNavigate();
  const { smileInput } = useParams();
  const { getDock } = apiConfig();
  const eventSourceRef = useRef(null);
  const logsEndRef = useRef(null);

  // Auto-scroll to bottom of logs
  useEffect(() => {
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs]);

  const startStreaming = useCallback((reqId) => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const url = `${API_BASE_URL}/data/stream/docking/logs/${reqId}`;
    console.log('Starting stream:', url);

    const eventSource = new EventSource(url);
    eventSourceRef.current = eventSource;

    eventSource.onmessage = (event) => {
      try {
        console.log('Stream message received:', event.data);
        const data = JSON.parse(event.data);
        if (data.error) {
          console.error('Stream error:', data.error);
          eventSource.close();
          return;
        }
        const newLogs = Object.values(data);
        if (newLogs.length > 0) {
          console.log('Updating logs with:', newLogs);
          setLogs((prev) => [...prev, ...newLogs]);
        }
      } catch (e) {
        console.error('Error parsing stream data:', e);
      }
    };

    eventSource.onerror = (err) => {
      console.error('EventSource failed:', err);
      eventSource.close();
    };
  }, []);

  const stopStreaming = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);
  
  const {
    files,
    previews,
    error: fileError,
    smileFileInputRef,
    pdbFileInputRef,
    handleFileUpload,
    removeFile,
    togglePreview,
    clearError
  } = useFileUpload();

  // Initialize SMILES from URL params
  useEffect(() => {
    if (smileInput && smileInput !== 'undefined') {
      // If we have SMILES from URL, we could set it to the file content
      // This maintains the original behavior
    }
  }, [smileInput]);

  const canPerformSearch = useMemo(() => {
    return files.smile.content && files.pdb.content;
  }, [files.smile.content, files.pdb.content]);

  const handleSearch = useCallback(async () => {
    if (!canPerformSearch) {
      setApiError('Please upload both SMILES and PDB files before docking');
      return;
    }

    setLoading(true);
    setSearchState(SEARCH_STATES.LOADING);
    setApiError(null);
    setLogs([]);

    const reqId = uuidv4();
    startStreaming(reqId);
    
    try {
      const data = await getDock(files.smile.content, files.pdb.content, reqId);
      
      if (data) {
        setSearchResult(data);
        setSearchState(SEARCH_STATES.COMPLETE);
      } else {
        throw new Error('No data returned from docking service');
      }
    } catch (error) {
      console.error('Docking error:', error);
      setSearchState(SEARCH_STATES.ERROR);
      setApiError(error.message || 'Failed to perform molecular docking. Please try again.');
    } finally {
      setLoading(false);
      stopStreaming();
    }
  }, [files.smile.content, files.pdb.content, getDock, canPerformSearch, startStreaming, stopStreaming]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const clearApiError = useCallback(() => {
    setApiError(null);
  }, []);

  // Progress indicator for upload status
  const getUploadProgress = useMemo(() => {
    const smileUploaded = files.smile.content ? 1 : 0;
    const pdbUploaded = files.pdb.content ? 1 : 0;
    return {
      completed: smileUploaded + pdbUploaded,
      total: 2,
      percentage: ((smileUploaded + pdbUploaded) / 2) * 100
    };
  }, [files.smile.content, files.pdb.content]);

  // Memoized components
  const ActionButtons = useMemo(() => (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
      <ActionButton
        variant="primary"
        label="Start Molecular Docking"
        onClick={handleSearch}
        isLoading={loading}
        disabled={!canPerformSearch || loading}
        textColor="white"
        sx={{ minWidth: 180, height: 44 }}
      />
      <ActionButton
        variant="secondary"
        label="Back"
        onClick={handleBack}
        disabled={loading}
        sx={{ minWidth: 100, height: 44 }}
      />
    </Box>
  ), [handleSearch, handleBack, loading, canPerformSearch]);

  const LoadingIndicator = useMemo(() => (
    loading && (
      <Box sx={{ 
        mt: 3, 
        p: 3,
        backgroundColor: '#F8FAFC',
        border: '1px solid #E5E7EB',
        borderRadius: 2,
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        gap: 2 
      }}>
        <LinearWithValueLabel />
        <Typography variant="body1" sx={{ color: '#6B7280', fontWeight: 500 }}>
          Performing molecular docking analysis...
        </Typography>
        <Typography variant="body2" sx={{ color: '#9CA3AF', textAlign: 'center' }}>
          This process may take several minutes depending on the complexity of your molecules
        </Typography>
      </Box>
    )
  ), [loading]);

  return (
    <Box sx={{ overflowX: 'hidden' }}>
      {/* Professional Header */}
      <HeaderContainer elevation={0}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                fontWeight: 600, 
                marginBottom: 1,
                fontSize: { xs: '1.5rem', sm: '2rem' }
              }}
            >
              Molecular Docking Analysis
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                opacity: 0.9,
                fontSize: '1.1rem',
                lineHeight: 1.5,
                marginBottom: 2
              }}
            >
              Upload SMILES and PDB files to perform high-precision molecular docking simulations
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <FeatureChip label="Protein-Ligand Docking" size="small" />
              <FeatureChip label="Binding Affinity" size="small" />
              <FeatureChip label="3D Structure Analysis" size="small" />
            </Box>
          </Box>
          <Box sx={{ 
            display: { xs: 'none', md: 'block' },
            opacity: 0.3,
            fontSize: '4rem',
            fontWeight: 300
          }}>
            🧬
          </Box>
        </Box>
      </HeaderContainer>

      {/* Error Snackbars */}
      <Snackbar 
        open={!!fileError} 
        autoHideDuration={6000} 
        onClose={clearError}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={clearError} severity="error" sx={{ fontWeight: 500 }}>
          {fileError}
        </Alert>
      </Snackbar>
      
      <Snackbar 
        open={!!apiError} 
        autoHideDuration={8000} 
        onClose={clearApiError}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={clearApiError} severity="error" sx={{ fontWeight: 500 }}>
          {apiError}
        </Alert>
      </Snackbar>

      {/* Main Content */}
      <ContentContainer elevation={0}>
        {/* Upload Progress Indicator */}
        {getUploadProgress.completed > 0 && (
          <Alert 
            severity="info" 
            sx={{ 
              mb: 3,
              backgroundColor: '#EFF6FF',
              borderColor: '#BFDBFE',
              color: '#1E40AF'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography>
                Upload Progress: <strong>{getUploadProgress.completed}</strong> of <strong>{getUploadProgress.total}</strong> files completed
              </Typography>
              <StatusChip 
                status={getUploadProgress.percentage === 100 ? 'uploaded' : 'processing'}
                label={`${getUploadProgress.percentage}%`} 
                size="small"
              />
            </Box>
          </Alert>
        )}

        {/* File Upload Section */}
        <UploadSection elevation={0}>
          <CardContent>
            <Typography 
              variant="h6" 
              sx={{ 
                marginBottom: 2, 
                color: '#374151',
                fontWeight: 600
              }}
            >
              File Upload Configuration
            </Typography>
            <Divider sx={{ marginBottom: 3, backgroundColor: '#E5E7EB' }} />

            <FieldGroupWrapper>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FileUploadBox
                  uploadedFile={files.smile}
                  onFileChange={handleFileUpload}
                  uploadName={UPLOAD_NAMES.SMILE}
                  fileInputRef={smileFileInputRef}
                  onRemove={() => removeFile(FILE_TYPES.SMILE)}
                  onPreview={() => togglePreview(FILE_TYPES.SMILE)}
                  fileType={FILE_TYPES.SMILE}
                />

                <FileUploadBox
                  uploadedFile={files.pdb}
                  onFileChange={handleFileUpload}
                  uploadName={UPLOAD_NAMES.PDB}
                  fileInputRef={pdbFileInputRef}
                  onRemove={() => removeFile(FILE_TYPES.PDB)}
                  onPreview={() => togglePreview(FILE_TYPES.PDB)}
                  fileType={FILE_TYPES.PDB}
                />
              </Box>
            </FieldGroupWrapper>

            <Divider sx={{ my: 3, backgroundColor: '#E5E7EB' }} />
            
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              {!canPerformSearch && (
                <Alert severity="warning" sx={{ mb: 2, width: '100%' }}>
                  Please upload both SMILES and PDB files to enable molecular docking analysis
                </Alert>
              )}
              
              {ActionButtons}
              {LoadingIndicator}
            </Box>
          </CardContent>
        </UploadSection>

        {/* Logs Section */}
        {(loading || logs.length > 0) && (
          <Box sx={{ marginBottom: 3 }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: '#1e1e1e',
                color: '#e0e0e0',
                borderRadius: 2,
                fontFamily: 'monospace',
                maxHeight: '200px',
                overflowY: 'auto',
                fontSize: '0.875rem',
                border: '1px solid #333'
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  color: '#90caf9',
                  mb: 1,
                  borderBottom: '1px solid #333',
                  pb: 0.5
                }}
              >
                Processing Logs
              </Typography>
              {logs.map((log, index) => (
                <Box key={index} sx={{ py: 0.25 }}>
                  <span style={{ color: '#66bb6a', marginRight: '8px' }}>➜</span>
                  {log}
                </Box>
              ))}
              {loading && (
                <Box sx={{ py: 0.25, fontStyle: 'italic', opacity: 0.7 }}>
                  <span style={{ display: 'inline-block', animation: 'pulse 1.5s infinite' }}>...</span>
                </Box>
              )}
              <div ref={logsEndRef} />
            </Paper>
          </Box>
        )}

        {/* Results Section */}
        {(searchResult || searchState === SEARCH_STATES.LOADING || searchState === SEARCH_STATES.ERROR) && (
          <Box>
            <Typography 
              variant="h6" 
              sx={{ 
                marginBottom: 2, 
                color: '#374151',
                fontWeight: 600
              }}
            >
              Docking Results
            </Typography>
            <Divider sx={{ marginBottom: 2, backgroundColor: '#E5E7EB' }} />

            {/* Results summary */}
            {searchResult && searchState === SEARCH_STATES.COMPLETE && (
              <Alert 
                severity="success" 
                sx={{ 
                  mb: 2,
                  backgroundColor: '#ECFDF5',
                  borderColor: '#A7F3D0',
                  color: '#065F46'
                }}
              >
                <Typography>
                  Molecular docking completed successfully! 
                  {Array.isArray(searchResult) && (
                    <> Found <strong>{searchResult.length}</strong> binding pose{searchResult.length !== 1 ? 's' : ''}</>
                  )}
                </Typography>
              </Alert>
            )}

            {/* No results message */}
            {searchState === SEARCH_STATES.COMPLETE && !searchResult && (
              <Alert 
                severity="info" 
                sx={{ 
                  mb: 2,
                  backgroundColor: '#EFF6FF',
                  borderColor: '#BFDBFE',
                  color: '#1E40AF'
                }}
              >
                No viable binding poses found. Consider adjusting your input files or parameters.
              </Alert>
            )}

            <ResultsSection>
              <AgGridWrapper
                data={searchResult}
                headers={molDockHeaders}
                loading={searchState === SEARCH_STATES.LOADING}
                error={searchState === SEARCH_STATES.ERROR}
              />
            </ResultsSection>
          </Box>
        )}
      </ContentContainer>

      {/* File Preview Modals */}
      <FileModal
        open={previews.smile}
        onClose={() => togglePreview(FILE_TYPES.SMILE)}
        content={files.smile.content || ''}
        title={`SMILES File: ${files.smile.name || 'Unknown'}`}
      />
      
      <FileModal
        open={previews.pdb}
        onClose={() => togglePreview(FILE_TYPES.PDB)}
        content={files.pdb.content || ''}
        title={`PDB File: ${files.pdb.name || 'Unknown'}`}
      />
    </Box>
  );
};

export default MolDock;
